import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/contexts/AuthContext';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { BackButton } from '@/components/BackButton';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import SimpleMarkdown from '@/components/SimpleMarkdown';
import { 
  Lightbulb,
  Gauge,
  Shield,
  Car,
  AlertTriangle,
  CloudRain,
  ParkingCircle,
  BookOpen,
  Wrench,
  Leaf,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Info,
  Flag,
  X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { API } from '@/config';
import { getPrefetched, getResolvedData, hasPrefetch } from '@/utils/dataPrefetcher';

// Translations for all 9 languages
const texts = {
  details: { 
    en: 'Details', sv: 'Detaljer', ar: 'التفاصيل', tr: 'Detaylar', 
    fa: 'جزئیات', so: 'Faahfaahin', pl: 'Szczegóły', es: 'Detalles', de: 'Details' 
  },
  relatedTips: { 
    en: '💡 Related Tips', sv: '💡 Liknande tips', ar: '💡 نصائح مشابهة', tr: '💡 İlgili İpuçları',
    fa: '💡 نکات مرتبط', so: '💡 Talooyinka la xiriira', pl: '💡 Powiązane wskazówki', es: '💡 Consejos relacionados', de: '💡 Verwandte Tipps'
  },
  loading: { 
    en: 'Loading...', sv: 'Laddar...', ar: 'جاري التحميل...', tr: 'Yükleniyor...',
    fa: 'در حال بارگذاری...', so: 'Soo dejinaya...', pl: 'Ładowanie...', es: 'Cargando...', de: 'Laden...'
  },
  tipNotFound: { 
    en: 'Tip Not Found', sv: 'Tipset hittades inte', ar: 'النصيحة غير موجودة', tr: 'İpucu Bulunamadı',
    fa: 'نکته یافت نشد', so: 'Talo lama helin', pl: 'Nie znaleziono wskazówki', es: 'Consejo no encontrado', de: 'Tipp nicht gefunden'
  },
  backToTips: { 
    en: 'Back to Tips', sv: 'Tillbaka till tips', ar: 'العودة للنصائح', tr: 'İpuçlarına Dön',
    fa: 'بازگشت به نکات', so: 'Ku noqo Talooyinka', pl: 'Powrót do wskazówek', es: 'Volver a consejos', de: 'Zurück zu Tipps'
  },
  noContent: { 
    en: 'No content available', sv: 'Inget innehåll tillgängligt', ar: 'لا يوجد محتوى متاح', tr: 'İçerik mevcut değil',
    fa: 'محتوایی موجود نیست', so: 'Nuxurka lama heli karo', pl: 'Brak treści', es: 'Sin contenido disponible', de: 'Kein Inhalt verfügbar'
  },
  reportError: { 
    en: 'Report Error', sv: 'Rapportera fel', ar: 'الإبلاغ عن خطأ', tr: 'Hata Bildir',
    fa: 'گزارش خطا', so: 'Soo sheeg khalad', pl: 'Zgłoś błąd', es: 'Reportar error', de: 'Fehler melden'
  },
  reportTitle: { 
    en: 'Report Error', sv: 'Rapportera fel', ar: 'الإبلاغ عن خطأ', tr: 'Hata Bildir',
    fa: 'گزارش خطا', so: 'Soo sheeg khalad', pl: 'Zgłoś błąd', es: 'Reportar error', de: 'Fehler melden'
  },
  helpUs: { 
    en: 'Help us improve this content', sv: 'Hjälp oss förbättra innehållet', ar: 'ساعدنا في تحسين هذا المحتوى', tr: 'İçeriği geliştirmemize yardımcı olun',
    fa: 'به ما کمک کنید محتوا را بهبود دهیم', so: 'Naga caawi inaad hagaajiso nuxurka', pl: 'Pomóż nam ulepszyć tę treść', es: 'Ayúdanos a mejorar este contenido', de: 'Helfen Sie uns, diesen Inhalt zu verbessern'
  },
  errorType: { 
    en: 'Error Type', sv: 'Feltyp', ar: 'نوع الخطأ', tr: 'Hata Türü',
    fa: 'نوع خطا', so: 'Nooca khaladka', pl: 'Typ błędu', es: 'Tipo de error', de: 'Fehlertyp'
  },
  additionalDetails: { 
    en: 'Additional Details (optional)', sv: 'Ytterligare detaljer (valfritt)', ar: 'تفاصيل إضافية (اختياري)', tr: 'Ek detaylar (isteğe bağlı)',
    fa: 'جزئیات اضافی (اختیاری)', so: 'Faahfaahin dheeraad (ikhtiyaar)', pl: 'Dodatkowe szczegóły (opcjonalnie)', es: 'Detalles adicionales (opcional)', de: 'Zusätzliche Details (optional)'
  },
  submitReport: { 
    en: 'Submit Report', sv: 'Skicka rapport', ar: 'إرسال البلاغ', tr: 'Raporu Gönder',
    fa: 'ارسال گزارش', so: 'Dir warbixinta', pl: 'Wyślij zgłoszenie', es: 'Enviar reporte', de: 'Bericht senden'
  },
  cancel: { 
    en: 'Cancel', sv: 'Avbryt', ar: 'إلغاء', tr: 'İptal',
    fa: 'لغو', so: 'Jooji', pl: 'Anuluj', es: 'Cancelar', de: 'Abbrechen'
  },
  selectError: { 
    en: 'Please select error type', sv: 'Välj feltyp', ar: 'الرجاء اختيار نوع الخطأ', tr: 'Lütfen hata türünü seçin',
    fa: 'لطفا نوع خطا را انتخاب کنید', so: 'Fadlan dooro nooca khaladka', pl: 'Wybierz typ błędu', es: 'Selecciona el tipo de error', de: 'Bitte Fehlertyp auswählen'
  },
  reportSuccess: { 
    en: 'Report submitted! Thank you', sv: 'Rapport skickad! Tack', ar: 'تم إرسال البلاغ! شكراً', tr: 'Rapor gönderildi! Teşekkürler',
    fa: 'گزارش ارسال شد! متشکریم', so: 'Warbixinta la diray! Mahadsanid', pl: 'Zgłoszenie wysłane! Dziękujemy', es: '¡Reporte enviado! Gracias', de: 'Bericht gesendet! Danke'
  },
  reportFailed: { 
    en: 'Error submitting report', sv: 'Fel vid skickande', ar: 'خطأ في الإرسال', tr: 'Gönderme hatası',
    fa: 'خطا در ارسال', so: 'Khalad markii la dirayay', pl: 'Błąd wysyłania', es: 'Error al enviar', de: 'Fehler beim Senden'
  }
};

// أنواع الأخطاء
const reportTypes = [
  { value: 'translation_error', label: { ar: 'خطأ في الترجمة', sv: 'Översättningsfel', en: 'Translation error', tr: 'Çeviri hatası', fa: 'خطای ترجمه', so: 'Khalad turjumaad', pl: 'Błąd tłumaczenia', es: 'Error de traducción', de: 'Übersetzungsfehler' } },
  { value: 'wrong_info', label: { ar: 'معلومات خاطئة', sv: 'Fel information', en: 'Wrong information', tr: 'Yanlış bilgi', fa: 'اطلاعات اشتباه', so: 'Macluumaad khaldan', pl: 'Błędne informacje', es: 'Información incorrecta', de: 'Falsche Information' } },
  { value: 'unclear_content', label: { ar: 'المحتوى غير واضح', sv: 'Otydligt innehåll', en: 'Unclear content', tr: 'Belirsiz içerik', fa: 'محتوای نامشخص', so: 'Nuxur aan caddayn', pl: 'Niejasna treść', es: 'Contenido poco claro', de: 'Unklarer Inhalt' } },
  { value: 'missing_content', label: { ar: 'محتوى مفقود', sv: 'Saknat innehåll', en: 'Missing content', tr: 'Eksik içerik', fa: 'محتوای گمشده', so: 'Nuxur maqan', pl: 'Brakująca treść', es: 'Contenido faltante', de: 'Fehlender Inhalt' } },
  { value: 'other', label: { ar: 'خطأ آخر', sv: 'Annat fel', en: 'Other error', tr: 'Diğer hata', fa: 'خطای دیگر', so: 'Khalad kale', pl: 'Inny błąd', es: 'Otro error', de: 'Anderer Fehler' } }
];

const getText = (key, lang) => texts[key]?.[lang] || texts[key]?.en || key;

const QuickTipDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const { token, user } = useAuth();
  const passedTip = location.state?.tipData || null;
  
  const findTipInList = () => {
    const listData = getResolvedData('quickTips');
    if (listData && Array.isArray(listData)) {
      return listData.find(t => t.id === id || (typeof t.title === 'string' ? t.title : (t.title?.sv || '')).normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') === id) || null;
    }
    return null;
  };
  
  const [tip, setTip] = useState(() => getResolvedData(`tip_${id}`) || passedTip || findTipInList());
  const [allTips, setAllTips] = useState([]);
  const [loading, setLoading] = useState(() => {
    if (getResolvedData(`tip_${id}`) || passedTip || findTipInList()) return false;
    if (hasPrefetch(`tip_${id}`)) return false;
    return true;
  });
  const [error, setError] = useState(null);
  const isRTL = ['ar', 'fa'].includes(i18n.language);
  const lang = i18n.language;
  
  const isSubscribed = user?.subscription_status === 'active' || user?.taxi_subscription_status === 'active' || user?.is_admin || user?.role === 'super_admin';
  const FREE_TIPS_LIMIT = user ? 3 : 2;
  
  // State للإبلاغ
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportType, setReportType] = useState('');
  const [reportDetails, setReportDetails] = useState('');

  // دالة إرسال الإبلاغ
  const handleReportSubmit = async () => {
    if (!reportType) {
      toast.error(getText('selectError', lang));
      return;
    }
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/site-reports`, {
        report_type: reportType,
        details: reportDetails,
        page_url: window.location.href,
        language: lang
      }, { headers });
      
      toast.success(getText('reportSuccess', lang));
      setShowReportModal(false);
      setReportType('');
      setReportDetails('');
    } catch (err) {
      console.error('Error submitting report:', err);
      toast.error(getText('reportFailed', lang));
    }
  };

  const categories = {
    speed: { 
      label: { en: 'Speed', sv: 'Hastighet', ar: 'السرعة', tr: 'Hız', fa: 'سرعت', so: 'Xawaare', pl: 'Prędkość', es: 'Velocidad', de: 'Geschwindigkeit' },
      icon: Gauge,
      color: 'from-blue-500 to-indigo-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-200'
    },
    safety: { 
      label: { en: 'Safety', sv: 'Säkerhet', ar: 'السلامة', tr: 'Güvenlik', fa: 'ایمنی', so: 'Badbaado', pl: 'Bezpieczeństwo', es: 'Seguridad', de: 'Sicherheit' },
      icon: Shield,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-700',
      borderColor: 'border-green-200'
    },
    rules: { 
      label: { en: 'Rules', sv: 'Regler', ar: 'القواعد', tr: 'Kurallar', fa: 'قوانین', so: 'Xeerarka', pl: 'Zasady', es: 'Reglas', de: 'Regeln' },
      icon: Car,
      color: 'from-orange-500 to-amber-500',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-700',
      borderColor: 'border-orange-200'
    },
    signs: { 
      label: { en: 'Signs', sv: 'Skyltar', ar: 'الإشارات', tr: 'İşaretler', fa: 'علائم', so: 'Calaamadaha', pl: 'Znaki', es: 'Señales', de: 'Schilder' },
      icon: AlertTriangle,
      color: 'from-yellow-500 to-amber-500',
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-700',
      borderColor: 'border-yellow-200'
    },
    weather: { 
      label: { en: 'Weather', sv: 'Väder', ar: 'الطقس', tr: 'Hava', fa: 'آب و هوا', so: 'Cimilada', pl: 'Pogoda', es: 'Clima', de: 'Wetter' },
      icon: CloudRain,
      color: 'from-slate-500 to-gray-600',
      bgColor: 'bg-slate-50',
      textColor: 'text-slate-700',
      borderColor: 'border-slate-200'
    },
    parking: { 
      label: { en: 'Parking', sv: 'Parkering', ar: 'الوقوف', tr: 'Park', fa: 'پارکینگ', so: 'Gaadiidka', pl: 'Parking', es: 'Estacionamiento', de: 'Parken' },
      icon: ParkingCircle,
      color: 'from-emerald-500 to-teal-500',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-700',
      borderColor: 'border-emerald-200'
    },
    exam: { 
      label: { en: 'Exam', sv: 'Prov', ar: 'الاختبار', tr: 'Sınav', fa: 'آزمون', so: 'Imtixaan', pl: 'Egzamin', es: 'Examen', de: 'Prüfung' },
      icon: BookOpen,
      color: 'from-lime-500 to-green-500',
      bgColor: 'bg-lime-50',
      textColor: 'text-lime-700',
      borderColor: 'border-lime-200'
    },
    vehicle: { 
      label: { en: 'Vehicle', sv: 'Fordon', ar: 'السيارة', tr: 'Araç', fa: 'خودرو', so: 'Gaadiidka', pl: 'Pojazd', es: 'Vehículo', de: 'Fahrzeug' },
      icon: Wrench,
      color: 'from-gray-500 to-zinc-600',
      bgColor: 'bg-gray-50',
      textColor: 'text-gray-700',
      borderColor: 'border-gray-200'
    },
    eco: { 
      label: { en: 'Eco', sv: 'Miljö', ar: 'بيئي', tr: 'Eko', fa: 'محیط زیست', so: 'Deegaan', pl: 'Eko', es: 'Eco', de: 'Öko' },
      icon: Leaf,
      color: 'from-green-600 to-emerald-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-700',
      borderColor: 'border-green-200'
    },
    general: { 
      label: { en: 'General', sv: 'Allmänt', ar: 'عام', tr: 'Genel', fa: 'عمومی', so: 'Guud', pl: 'Ogólne', es: 'General', de: 'Allgemein' },
      icon: Lightbulb,
      color: 'from-purple-500 to-violet-500',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-700',
      borderColor: 'border-purple-200'
    }
  };

  useEffect(() => {
    fetchTipDetails();
    fetchAllTips();
  }, [id]);

  // تحقق من صلاحية الوصول للنصيحة
  const [isPaidTip, setIsPaidTip] = useState(false);
  useEffect(() => {
    if (allTips.length > 0 && tip && !isSubscribed) {
      const currentIndex = allTips.findIndex(t => t.id === tip.id);
      setIsPaidTip(currentIndex >= FREE_TIPS_LIMIT);
    } else {
      setIsPaidTip(false);
    }
  }, [allTips, tip, isSubscribed]);

  const generateSlugFromTitle = (title) => {
    if (!title) return '';
    const sv = typeof title === 'string' ? title : (title.sv || title.en || '');
    return sv.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
      .replace(/[^a-z0-9\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  };

  const fetchTipDetails = async () => {
    const hasInitialData = !!tip;
    // 1. Try hover-based prefetch
    const prefetched = await getPrefetched(`tip_${id}`);
    if (prefetched) {
      setTip(prefetched);
      setLoading(false);
    } else {
      // 2. Try finding in prefetched list data (by ID or by slug) - wait for prefetch to complete
      const allTipsData = await getPrefetched('quickTips');
      if (allTipsData && Array.isArray(allTipsData)) {
        const found = allTipsData.find(t => t.id === id || generateSlugFromTitle(t.title) === id);
        if (found) {
          setTip(found);
          setLoading(false);
        }
      }
    }
    // 3. ALWAYS fetch full multilingual version in background (ensures correct hreflang for SEO)
    if (!hasInitialData && !tip) setLoading(true);
    try {
      let response;
      try {
        response = await axios.get(`${API}/quick-tips/${id}`);
      } catch {
        response = await axios.get(`${API}/quick-tips/by-slug/${id}`);
      }
      // Only update if API returned richer data (dict title vs string)
      if (response.data && typeof response.data.title === 'object') {
        setTip(response.data);
      } else if (!tip) {
        setTip(response.data);
      }
    } catch (error) {
      console.error('Error fetching tip:', error);
      if (!hasInitialData && !tip) setError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTips = async () => {
    try {
      const prefetched = await getPrefetched('quickTips');
      if (prefetched) {
        setAllTips(prefetched);
      } else {
        const response = await axios.get(`${API}/quick-tips?lang=${i18n.language || 'sv'}`);
        setAllTips(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching all tips:', error);
    }
  };

  const getCurrentIndex = () => {
    if (!tip) return -1;
    const idx = allTips.findIndex(t => t.id === tip.id);
    if (idx !== -1) return idx;
    return allTips.findIndex(t => generateSlug(t.title) === id);
  };
  
  const canAccessTip = (index) => {
    if (isSubscribed) return true;
    return index < FREE_TIPS_LIMIT;
  };
  
  const goToNextTip = () => {
    const currentIndex = getCurrentIndex();
    const nextIndex = currentIndex + 1;
    
    if (nextIndex < allTips.length) {
      if (canAccessTip(nextIndex)) {
        navigate(`${getTipPath()}/${allTips[nextIndex].id}`);
      } else {
        navigate('/pricing');
      }
    }
  };

  const goToPreviousTip = () => {
    const currentIndex = getCurrentIndex();
    const prevIndex = currentIndex - 1;
    
    if (prevIndex >= 0) {
      if (canAccessTip(prevIndex)) {
        navigate(`${getTipPath()}/${allTips[prevIndex].id}`);
      } else {
        navigate('/pricing');
      }
    }
  };

  const getRelatedTips = () => {
    if (!tip) return [];
    return allTips
      .filter(t => t.category === tip.category && t.id !== tip.id)
      .slice(0, 4);
  };

  if ((loading && !tip) || (!tip && !error)) {
    return null;
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900" dir={isRTL ? 'rtl' : 'ltr'}>
        <Helmet>
          <meta name="robots" content="noindex, follow" />
          <title>{getText('tipNotFound', lang)} | DriveMaster</title>
        </Helmet>
        <Navbar />
        <main className="flex-grow container mx-auto px-4 py-8 mt-20">
          <Card className="p-12 text-center max-w-md mx-auto dark:bg-slate-800 dark:border-slate-700">
            <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">
              {getText('tipNotFound', lang)}
            </h2>
            <Button onClick={() => navigate('/snabba-rad')} className="mt-4">
              <ArrowLeft className={`w-4 h-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
              {getText('backToTips', lang)}
            </Button>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryInfo = categories[tip.category] || categories.general;
  const CategoryIcon = categoryInfo.icon;
  const currentIndex = getCurrentIndex();
  const relatedTips = getRelatedTips();

  const getTipTitle = () => {
    if (tip.title && typeof tip.title === 'object') return tip.title[lang] || tip.title.sv || tip.title.en || '';
    return tip.title || '';
  };
  const getTipContent = () => {
    if (tip.content && typeof tip.content === 'object') return tip.content[lang] || tip.content.sv || tip.content.en || '';
    return tip.content || '';
  };
  const generateSlug = (name) => {
    if (!name) return '';
    const txt = typeof name === 'string' ? name : (name.sv || name.en || '');
    return txt.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
      .replace(/[^a-z0-9\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  };
  // Per-language slug from that language's title (fallback to Swedish slug if empty)
  const titleObj = (tip.title && typeof tip.title === 'object') ? tip.title : { sv: tip.title || '' };
  const svTipSlug = generateSlug(titleObj.sv || '');
  const getLangTipSlug = (l) => generateSlug(titleObj[l] || '') || svTipSlug;
  const getTipPath = () => {
    const langPaths = {
      sv: '/snabba-rad', ar: '/ar/nasaeh-sareea', en: '/en/quick-tips',
      tr: '/tr/hizli-ipuclari', fa: '/fa/nookat-sari', so: '/so/talooyinka-degdegga',
      de: '/de/schnelle-tipps', es: '/es/consejos-rapidos', pl: '/pl/szybkie-porady',
    };
    return langPaths[lang] || langPaths.sv;
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar />
      
      <main className="flex-grow container mx-auto px-4 py-8 mt-20">
        {/* SEO Structured Data - Language-aware */}
        <Helmet>
          <title>{getTipTitle()} - {lang === 'ar' ? 'نصائح سريعة للقيادة' : lang === 'sv' ? 'Snabbt tips' : 'Quick Driving Tip'} | DriveMaster</title>
          <meta name="description" content={`${getTipTitle()}: ${getTipContent().substring(0, 100)} - DriveMaster`} />
          <meta property="og:title" content={`${getTipTitle()} | DriveMaster`} />
          <meta property="og:description" content={`${getTipTitle()}: ${getTipContent().substring(0, 120)}`} />
          <meta property="og:image" content="https://drivemaster.se/logo192.png" />
          <meta property="og:image:alt" content={getTipTitle()} />
          <meta property="og:type" content="article" />
          <meta property="og:site_name" content="DriveMaster" />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:title" content={`${getTipTitle()} | DriveMaster`} />
          <link rel="canonical" href={`https://drivemaster.se${getTipPath()}/${getLangTipSlug(lang)}`} />
          {Object.entries({
            sv: 'snabba-rad', ar: 'ar/nasaeh-sareea', en: 'en/quick-tips',
            tr: 'tr/hizli-ipuclari', fa: 'fa/nookat-sari', so: 'so/talooyinka-degdegga',
            de: 'de/schnelle-tipps', es: 'es/consejos-rapidos', pl: 'pl/szybkie-porady'
          }).map(([hLang, hPath]) => (
            <link key={hLang} rel="alternate" hrefLang={hLang} href={`https://drivemaster.se/${hPath}/${getLangTipSlug(hLang)}`} />
          ))}
          <link rel="alternate" hrefLang="x-default" href={`https://drivemaster.se/snabba-rad/${svTipSlug}`} />
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "name": getTipTitle(),
            "headline": getTipTitle(),
            "description": getTipContent().substring(0, 200),
            "author": { "@type": "Organization", "name": "DriveMaster", "url": "https://drivemaster.se" },
            "publisher": { "@type": "Organization", "name": "DriveMaster" },
            "inLanguage": lang,
            "keywords": `${getTipTitle()}, ${categoryInfo.label?.[lang] || ''}, ${lang === 'ar' ? 'نصائح القيادة, السويد' : lang === 'sv' ? 'körtips, Sverige' : 'driving tips, Sweden'}`
          })}</script>
        </Helmet>

        <BackButton to="/snabba-rad" className="mb-6" />

        {/* Main Content Card */}
        <Card className={`overflow-hidden border-2 ${categoryInfo.borderColor} dark:border-slate-700 shadow-xl max-w-4xl mx-auto relative`}>
          {/* Header with gradient */}
          <div className={`bg-gradient-to-r ${categoryInfo.color} dark:opacity-80 p-6 text-white`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <CategoryIcon className="w-6 h-6" />
                </div>
                <div>
                  <Badge className="bg-white/20 text-white border-0 mb-1">
                    {categoryInfo.label[lang] || categoryInfo.label.en}
                  </Badge>
                  <p className="text-white/80 text-sm">
                    {lang === 'ar' ? `${currentIndex + 1} من ${allTips.length}` : `${currentIndex + 1} / ${allTips.length}`}
                  </p>
                </div>
              </div>
              
              {/* Navigation arrows and Report button */}
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goToPreviousTip}
                  disabled={currentIndex <= 0}
                  className="bg-white/20 hover:bg-white/30 text-white disabled:opacity-50"
                >
                  {isRTL ? <ArrowRight className="w-5 h-5" /> : <ArrowLeft className="w-5 h-5" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goToNextTip}
                  disabled={currentIndex >= allTips.length - 1}
                  className="bg-white/20 hover:bg-white/30 text-white disabled:opacity-50"
                >
                  {isRTL ? <ArrowLeft className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
                </Button>
                
                {/* زر الإبلاغ */}
                <button
                  onClick={() => setShowReportModal(true)}
                  className="p-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg hover:shadow-xl transition-all"
                  title={getText('reportError', lang)}
                  data-testid="report-error-btn"
                >
                  <Flag className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-8 bg-white dark:bg-slate-800">
            {/* Title */}
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-6">
              {getTipTitle()}
            </h1>
            
            {/* Content */}
            <div className={`${categoryInfo.bgColor} dark:bg-slate-700/50 ${categoryInfo.textColor} dark:text-slate-300 rounded-xl p-6`}>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Info className="w-5 h-5" />
                {getText('details', lang)}
              </h3>
              {getTipContent() ? (
                <SimpleMarkdown
                  source={getTipContent()}
                  className="text-base sm:text-lg leading-relaxed prose-quicktip"
                />
              ) : (
                <div className="text-base sm:text-lg leading-relaxed">
                  {getText('noContent', lang)}
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Related Tips */}
        {relatedTips.length > 0 && (
          <div className="mt-12 max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">
              {getText('relatedTips', lang)}
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedTips.map((relatedTip) => {
                const relatedCatInfo = categories[relatedTip.category] || categories.general;
                const RelatedIcon = relatedCatInfo.icon;
                return (
                  <Card
                    key={relatedTip.id}
                    className="cursor-pointer hover:shadow-lg transition-all hover:scale-105 overflow-hidden dark:bg-slate-800 dark:border-slate-700"
                    onClick={() => navigate(`/snabba-rad/${relatedTip.id}`)}
                  >
                    <div className={`bg-gradient-to-r ${relatedCatInfo.color} dark:opacity-70 p-4 flex items-center justify-center`}>
                      <RelatedIcon className="w-12 h-12 text-white" />
                    </div>
                    <div className="p-3">
                      <p className="font-medium text-sm text-slate-900 dark:text-slate-200 line-clamp-2">
                        {relatedTip.title?.[lang] || relatedTip.title?.sv}
                      </p>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </main>

      <Footer />
      
      {/* مودال الإبلاغ */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-3xl max-w-md w-full p-6 relative shadow-2xl" dir={isRTL ? 'rtl' : 'ltr'}>
            <button
              onClick={() => { setShowReportModal(false); setReportType(''); setReportDetails(''); }}
              className="absolute top-4 end-4 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full mb-4">
                <Flag className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">{getText('reportTitle', lang)}</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm">{getText('helpUs', lang)}</p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">{getText('errorType', lang)}</label>
              <div className="space-y-2">
                {reportTypes.map((type) => (
                  <label key={type.value} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${reportType === type.value ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/30' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'}`}>
                    <input type="radio" name="reportType" value={type.value} checked={reportType === type.value} onChange={(e) => setReportType(e.target.value)} className="w-4 h-4 text-orange-500" />
                    <span className="text-sm dark:text-slate-300">{type.label[lang] || type.label.en}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">{getText('additionalDetails', lang)}</label>
              <textarea value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} placeholder={texts.noContent?.[lang] || 'Details...'} className="w-full p-3 border-2 border-slate-200 dark:border-slate-700 dark:bg-slate-700 dark:text-slate-100 rounded-xl resize-none h-24 focus:border-orange-500 focus:outline-none" />
            </div>
            
            <div className="flex gap-3">
              <Button onClick={handleReportSubmit} className="flex-1 h-12 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold">{getText('submitReport', lang)}</Button>
              <Button onClick={() => { setShowReportModal(false); setReportType(''); setReportDetails(''); }} variant="outline" className="flex-1 h-12 rounded-xl border-2">{getText('cancel', lang)}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickTipDetailPage;
